import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useLeadLists } from '@/hooks/use-lead-lists.hook';
import { cn } from '@/lib/utils';
import { importableLists } from '@/utils/extension-leads.utils';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  iconArrowLeft,
  iconArrowRight,
  iconFolderPlus,
  iconLayerGroup,
  iconMagnifyingGlass,
  iconPlus,
  iconSpinnerThird,
} from '@/lib/icons';

type TargetPanel =
  | 'import-csv'
  | 'manual-entry'
  | 'import-crm'
  | 'people-search';

interface ListSelectionPopoverProps {
  children: React.ReactNode;
  targetPanel: TargetPanel;
  /** Callback after list is created (optional) */
  onListCreated?: (listId: string) => void;
  /** Additional class name for the popover content */
  contentClassName?: string;
}

type PopoverMode = 'initial' | 'new' | 'existing';

// Accent color for the popover
const POPOVER_COLOR = '#3E8BFF';

/**
 * Popover that prompts user to select a new or existing list
 * before navigating to the actions drawer with a specific panel open.
 */
export function ListSelectionPopover({
  children,
  targetPanel,
  onListCreated,
  contentClassName,
}: ListSelectionPopoverProps) {
  const navigate = useNavigate();
  const { leadLists, createList, isCreating } = useLeadLists();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<PopoverMode>('initial');
  const [newListName, setNewListName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      // Reset after animation completes
      setTimeout(() => {
        setMode('initial');
        setNewListName('');
        setSearchQuery('');
      }, 150);
    }
  };

  const handleCreateAndNavigate = async () => {
    if (!newListName.trim() || isCreating) return;

    try {
      const list = await createList({
        name: newListName.trim(),
        origin: 'manual',
      });
      onListCreated?.(list.id);
      navigate({
        to: `/lists/${list.id}`,
        search: { openDrawer: targetPanel },
      });
      setOpen(false);
    } catch {
      // Error is handled by useLeadLists hook (toast)
    }
  };

  const handleSelectExisting = (listId: string) => {
    navigate({ to: `/lists/${listId}`, search: { openDrawer: targetPanel } });
    setOpen(false);
  };

  const filteredLists = importableLists(leadLists || []).filter((list) =>
    list.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className={cn(
          'w-72 overflow-hidden p-0',
          'backdrop-blur-xl',
          'border border-black/5 dark:border-white/8',
          contentClassName
        )}
        align='start'
        sideOffset={8}
      >
        {mode === 'initial' && (
          <div className='p-3'>
            <div className='mb-3 flex items-center gap-2'>
              <div
                className={cn('rounded-md p-1.5', 'shadow-xs')}
                style={{
                  backgroundColor: `${POPOVER_COLOR}15`,
                }}
              >
                <AppIcon
                  icon={iconLayerGroup}
                  className='h-3.5 w-3.5'
                  style={{ color: POPOVER_COLOR }}
                />
              </div>
              <div>
                <p className='text-[13px] font-semibold text-foreground'>
                  Choose Destination
                </p>
                <p className='text-[11px] text-muted-foreground'>
                  Where should we add the leads?
                </p>
              </div>
            </div>

            <div className='space-y-1.5'>
              <button
                type='button'
                onClick={() => setMode('new')}
                className={cn(
                  'group relative w-full text-left',
                  'rounded-lg border p-2.5 transition-all duration-200',
                  'bg-white/70 dark:bg-white/[0.02]',
                  'border-border/50 hover:border-primary/30 hover:shadow-xs'
                )}
              >
                <div className='flex items-center gap-2.5'>
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-md',
                      'bg-linear-to-br from-green-500/10 to-green-500/5',
                      'border border-green-500/20'
                    )}
                  >
                    <AppIcon
                      icon={iconFolderPlus}
                      className='text-green-600'
                      style={{ fontSize: 14 }}
                    />
                  </div>
                  <div className='flex-1'>
                    <p className='text-[13px] font-medium text-foreground'>
                      Create new list
                    </p>
                    <p className='text-[11px] text-muted-foreground'>
                      Start fresh with a new lead list
                    </p>
                  </div>
                  <AppIcon
                    icon={iconArrowRight}
                    className='text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100'
                    style={{ fontSize: 14 }}
                  />
                </div>
              </button>

              <button
                type='button'
                onClick={() => setMode('existing')}
                className={cn(
                  'group relative w-full text-left',
                  'rounded-lg border p-2.5 transition-all duration-200',
                  'bg-white/70 dark:bg-white/[0.02]',
                  'border-border/50 hover:border-primary/30 hover:shadow-xs'
                )}
              >
                <div className='flex items-center gap-2.5'>
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-md',
                      'bg-linear-to-br from-blue-500/10 to-blue-500/5',
                      'border border-blue-500/20'
                    )}
                  >
                    <AppIcon
                      icon={iconPlus}
                      className='text-blue-600'
                      style={{ fontSize: 14 }}
                    />
                  </div>
                  <div className='flex-1'>
                    <p className='text-[13px] font-medium text-foreground'>
                      Import to existing list
                    </p>
                    <p className='text-[11px] text-muted-foreground'>
                      Choose from your existing lists
                    </p>
                  </div>
                  <AppIcon
                    icon={iconArrowRight}
                    className='text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100'
                    style={{ fontSize: 14 }}
                  />
                </div>
              </button>
            </div>
          </div>
        )}

        {mode === 'new' && (
          <div className='p-4'>
            <button
              type='button'
              onClick={() => setMode('initial')}
              className='mb-3 flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground'
            >
              <AppIcon icon={iconArrowLeft} className='h-3 w-3' />
              Back
            </button>

            <div className='mb-4 flex items-center gap-2.5'>
              <div
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-md shadow-xs',
                  'border border-green-500/20 bg-green-500/10'
                )}
              >
                <AppIcon
                  icon={iconFolderPlus}
                  className='text-green-500'
                  style={{ fontSize: 14 }}
                />
              </div>
              <div className='min-w-0'>
                <p className='text-[13px] leading-tight font-semibold text-foreground'>
                  Create new list
                </p>
                <p className='text-[11px] text-muted-foreground'>
                  Give your list a memorable name
                </p>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateAndNavigate();
              }}
              className='space-y-3'
            >
              <div className='space-y-1.5'>
                <Label
                  htmlFor='newListName'
                  className='text-[12px] font-medium text-muted-foreground'
                >
                  List name
                </Label>
                <Input
                  id='newListName'
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder='e.g. Q1 Outbound Targets'
                  autoFocus
                  className={cn(
                    'h-9 rounded-lg text-[13px]',
                    'bg-white/70 dark:bg-white/[0.02]',
                    'border-border/50 focus-visible:border-primary/40 focus-visible:ring-primary/20'
                  )}
                />
              </div>
              <Button
                type='submit'
                size='sm'
                disabled={!newListName.trim() || isCreating}
                className='group h-9 w-full rounded-lg font-medium'
              >
                {isCreating ? (
                  <>
                    <AppIcon
                      icon={iconSpinnerThird}
                      className='mr-2 animate-spin'
                      style={{ fontSize: 16 }}
                    />
                    Creating...
                  </>
                ) : (
                  <>
                    Create & Continue
                    <AppIcon
                      icon={iconArrowRight}
                      className='ml-1.5 transition-transform group-hover:translate-x-0.5'
                      style={{ fontSize: 14 }}
                    />
                  </>
                )}
              </Button>
            </form>
          </div>
        )}

        {mode === 'existing' && (
          <div className='p-4'>
            <button
              type='button'
              onClick={() => setMode('initial')}
              className='mb-3 flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground'
            >
              <AppIcon icon={iconArrowLeft} className='h-3 w-3' />
              Back
            </button>

            <div className='mb-4 flex items-center gap-2.5'>
              <div
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-md shadow-xs',
                  'border border-blue-500/20 bg-blue-500/10'
                )}
              >
                <AppIcon
                  icon={iconLayerGroup}
                  className='h-3.5 w-3.5 text-blue-500'
                />
              </div>
              <div className='min-w-0'>
                <p className='text-[13px] leading-tight font-semibold text-foreground'>
                  Select a list
                </p>
                <p className='text-[11px] text-muted-foreground'>
                  {(() => {
                    const n = leadLists?.length ?? 0;
                    return n === 1
                      ? '1 list available'
                      : `${n} lists available`;
                  })()}
                </p>
              </div>
            </div>

            <div className='space-y-2.5'>
              <div className='relative'>
                <AppIcon
                  icon={iconMagnifyingGlass}
                  className='absolute top-1/2 left-2.5 -translate-y-1/2 text-muted-foreground'
                  style={{ fontSize: 14 }}
                />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder='Find lists...'
                  className={cn(
                    'h-9 rounded-lg pl-8 text-[13px]',
                    'bg-white/70 dark:bg-white/[0.02]',
                    'border-border/50 focus-visible:border-primary/40 focus-visible:ring-primary/20'
                  )}
                  autoFocus
                />
              </div>

              <div className='-mr-1 max-h-52 overflow-y-auto pr-1'>
                {filteredLists.length === 0 ? (
                  <div className='flex flex-col items-center justify-center rounded-xl border border-dashed border-border/50 py-8 text-center'>
                    <AppIcon
                      icon={iconMagnifyingGlass}
                      className='mb-2 text-muted-foreground/50'
                      style={{ fontSize: 20 }}
                    />
                    <p className='text-sm text-muted-foreground'>
                      {searchQuery ? 'No lists found' : 'No lists yet'}
                    </p>
                  </div>
                ) : (
                  <ul className='space-y-1'>
                    {filteredLists.map((list) => {
                      const count = list.prospect_count ?? 0;
                      return (
                        <li key={list.id}>
                          <button
                            type='button'
                            onClick={() => handleSelectExisting(list.id)}
                            className={cn(
                              'group flex w-full items-center gap-2.5 rounded-lg border px-2 py-2 text-left transition-all duration-150',
                              'border-transparent bg-transparent',
                              'hover:border-border/60 hover:bg-white/80 dark:hover:bg-white/[0.04]'
                            )}
                          >
                            <div
                              className={cn(
                                'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
                                'border border-border/50 bg-muted/40 text-muted-foreground',
                                'group-hover:border-blue-500/30 group-hover:bg-blue-500/10 group-hover:text-blue-500'
                              )}
                            >
                              <AppIcon
                                icon={iconLayerGroup}
                                className='h-3.5 w-3.5'
                              />
                            </div>
                            <div className='min-w-0 flex-1'>
                              <span className='block truncate text-[13px] font-medium text-foreground'>
                                {list.name}
                              </span>
                              <span className='block text-[11px] text-muted-foreground'>
                                {count} {count === 1 ? 'lead' : 'leads'}
                              </span>
                            </div>
                            <AppIcon
                              icon={iconArrowRight}
                              className={cn(
                                'shrink-0 text-muted-foreground',
                                'translate-x-[-4px] opacity-0 transition-all duration-150',
                                'group-hover:translate-x-0 group-hover:text-foreground group-hover:opacity-100'
                              )}
                              style={{ fontSize: 14 }}
                            />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
